import { Component, inject, OnInit, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute } from "@angular/router";
import { faPencil } from "@fortawesome/free-solid-svg-icons";

import { ProjectService } from "../../../shared/services/project.service";
import { ImageService } from "../../../shared/services/image.service";
import { ExternalpageSingle } from "../../../class/externalpage-single";
import { AuthenticationService } from "../../../shared/services/authentication.service";

@Component({
  selector: "app-page-project",
  templateUrl: "./page-project.component.html",
  styleUrls: ["./page-project.component.scss"],
  standalone: false,
})
export class PageProjectComponent extends ExternalpageSingle implements OnInit {
  override readonly maxRelatedDataItems = 6;
  readonly window = window;
  readonly faPencil = faPencil;

  private readonly projectService = inject(ProjectService);
  private readonly route = inject(ActivatedRoute);
  readonly imageService = inject(ImageService);
  private readonly authService = inject(AuthenticationService);

  public isStudentLoggedIn = signal(false);
  public isGuest = signal(true);
  public relatedData = signal<any[]>([]);

  constructor() {
    super();
    this.projectService.setHomeData();
  }

  ngOnInit(): void {
    // Only load data if entity hasn't been provided by parent (dynamic component loading)
    if (!this.entity || !this.entity.id) {
      this.route.params
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((routeParams) => {
          this.onIdChange();
          this.loadSingle(routeParams["id"]);
        });
    } else {
      // Entity data provided by parent component, just run callbacks
      this.parseImages();
      this.loadCallback();
      this.setMetaTags();
    }

    // Check if a user is authenticated and set loggedIn state
    this.authService.profile$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.isGuest.set(result == null);
      });

    this.authService.studentprofile$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result != null) {
          this.isStudentLoggedIn.set(true);
        }
      });
  }

  override loadCallback(): void {
    this.setRelatedData(this.entity.id);
  }

  setRelatedData(id: string | null): void {
    this.relatedData.set([]);
    const tmp = this.projectService.homeData;
    if (tmp.length === 0) {
      this.projectService.$homeDataSubject
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          const items: any[] = [];
          for (const item of this.projectService.homeData) {
            if (item["id"] !== id && items.length < this.maxRelatedDataItems) {
              items.push(item);
            }
          }
          this.relatedData.set(items);
        });
    } else {
      const items: any[] = [];
      for (const item of tmp) {
        if (item["id"] !== id && items.length < this.maxRelatedDataItems) {
          items.push(item);
        }
      }
      this.relatedData.set(items);
    }
  }
}

export interface SingleProject {
  id: "";
  title: "";
  content: "";
  imageUri: "";
  images: any;
}
